import { Dictionary } from "lodash";
import { TextChip, BoolChip, ArrayChip, RangeChip, RatingChip, NumericChip } from "marketplace/_views/filters/filter-chip";

export type FilterChipType = TextChip | BoolChip | ArrayChip | RangeChip | RatingChip | NumericChip;

export class FilterChips {
  public get chips() {
    return this._chips;
  }

  public constructor(filterChips: FilterChipType[] = []) {
    this._chips = filterChips;
  }

  public findByName(filterName: string): FilterChipType | undefined {
    return this._chips.find(chip => chip.filterName === filterName);
  }

  public add(chip: FilterChipType) {
    this._chips.push(chip);
  }

  public addRange(chips: FilterChipType[]) {
    this._chips = chips;
  }

  public remove(filterName: string): boolean {
    let index = this._chips.findIndex(chip => chip.filterName === filterName);

    if (index === -1) return false;
    this._chips.splice(index, 1);

    return true;
  }

  public enable(filterName: string): boolean {
    let currentChip = this.findByName(filterName);

    if (!currentChip) return false;

    currentChip.enable();

    return true;
  }

  public disable(filterName: string): boolean {
    let currentChip = this.findByName(filterName);

    if (!currentChip) return false;

    currentChip.disable();

    return true;
  }

  public disableAll(): void {
    for (const chip of this._chips) {
      chip.disable();
    }
  }

  public updateValue(filterName: string, value: string | string[]): boolean {
    let currentChip = this.findByName(filterName);

    if (!currentChip) return false;
    
    if (currentChip instanceof ArrayChip || currentChip instanceof RangeChip) {
      if (!Array.isArray(value)) value = [value];
      currentChip.updateValue(value);
    } else {
      currentChip.updateValue(value.toString());
    }

    return true;
  }

  public toDictionary() : Dictionary<string> {
    let dictionary: Dictionary<string> = {};

    for (const chip of this._chips) {
      if (!chip.enabled) {
        continue;
      }

      dictionary[chip.filterName] = chip.value?.toString() ?? '';
    }

    return dictionary;
  }

  private _chips: FilterChipType[];
}

export function cloneFilterChip(chip: FilterChipType): FilterChipType {
  if (chip instanceof TextChip) {
    const cloned = new TextChip(chip.filterName, chip.title, chip.enabled, chip.value);
    cloned.label = chip.label;
    cloned.hidden = chip.hidden;
    return cloned;
  }

  if (chip instanceof BoolChip) {
    const cloned = new BoolChip(chip.filterName, chip.title, chip.enabled);
    cloned.value = chip.value;
    cloned.label = chip.label;
    cloned.hidden = chip.hidden;
    return cloned;
  }

  if (chip instanceof ArrayChip) {
    const cloned = new ArrayChip(chip.filterName, chip.title, chip.enabled);
    cloned.value = Array.isArray(chip.value) ? [...chip.value] : [];
    cloned.label = chip.label;
    cloned.hidden = chip.hidden;
    cloned.freeSolo = chip.freeSolo;
    cloned.multiple = chip.multiple;
    cloned.options = chip.options ? [...chip.options] : undefined;
    return cloned;
  }

  if (chip instanceof RangeChip) {
    const cloned = new RangeChip(chip.filterName, chip.title, chip.enabled);
    // The constructor's default is an empty range; the billing-rate filter's is 0 to 999. Losing it
    // left a copy with no range to draw, which never showed while the slider hid behind a switch.
    Object.assign(cloned, { default: Array.isArray(chip.default) ? [...chip.default] : chip.default });
    cloned.value = Array.isArray(chip.value) ? [...chip.value] : chip.value;
    cloned.label = chip.label;
    cloned.hidden = chip.hidden;
    cloned.min = chip.min;
    cloned.max = chip.max;
    cloned.steps = chip.steps;
    return cloned;
  }

  if (chip instanceof RatingChip) {
    const cloned = new RatingChip(chip.filterName, chip.title, chip.enabled);
    cloned.value = chip.value;
    cloned.label = chip.label;
    cloned.hidden = chip.hidden;
    cloned.precision = chip.precision;
    return cloned;
  }

  const cloned = new NumericChip(chip.filterName, chip.title, chip.enabled);
  cloned.value = chip.value;
  cloned.label = chip.label;
  cloned.hidden = chip.hidden;
  return cloned;
}

export function cloneFilterChips(chips: FilterChipType[]): FilterChipType[] {
  return chips.map(cloneFilterChip);
}
